import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import { useScreenPermissions } from '../components/useScreenPermissions';

import PurchaseOrderViewModal from './PurchaseOrderViewModal';
import MaterialReceiptViewModal from './MaterialReceiptViewModal';
import DeliveryNoteViewModal from './DeliveryNoteViewModal';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const TrackDocumentIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

const HidePanelIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

// Modern Error Alert Icon
const ErrorAlertIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" stroke="currentColor" fill="none" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// Document type icons
const SalesOrderIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

const PurchaseOrderIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2l3.54 6.54-2.32 2.32L6 22h12l-1.22-11.14-2.32-2.32L18 2H6z"></path>
    <line x1="12" y1="22" x2="12" y2="16"></line>
    <line x1="9" y1="16" x2="15" y2="16"></line>
  </svg>
);

const MRNIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

const DeliveryNoteIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

// Modern Error Popup Component
const ErrorPopup = ({ message, isVisible, onClose }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 4000); // Auto-close after 4 seconds
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop without blur */}
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose}></div>
      
      {/* Modern Popup Card */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full transform transition-all duration-300 animate-fadeIn">
        {/* Decorative top border */}
        <div className="h-1 w-full bg-gradient-to-r from-red-500 to-red-600 rounded-t-2xl"></div>
        
        <div className="p-6">
          <div className="flex items-start">
            {/* Icon with gradient background */}
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
                <ErrorAlertIcon className="h-6 w-6 text-white" />
              </div>
            </div>
            
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-bold text-gray-900">Access Denied</h3>
              <div className="mt-2">
                <p className="text-sm text-gray-600">{message}</p>
              </div>
            </div>
            
            {/* Close button */}
            <div className="ml-4 flex-shrink-0 flex">
              <button
                className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                onClick={onClose}
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 border border-transparent rounded-lg shadow-md hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all"
              onClick={onClose}
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const API_URL = '/api';

// SalesOrderViewModal Component with tracking
const SalesOrderViewModal = ({ open, onClose, soId }) => {
  const [soDetails, setSoDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    customers: [],
    products: [],
  });
  
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState(null);
  const [showTrackingPanel, setShowTrackingPanel] = useState(false);
  
  // State for document modals
  const [activeDocumentTab, setActiveDocumentTab] = useState(null);

  // State for error popup
  const [errorPopup, setErrorPopup] = useState({
    isVisible: false,
    message: ''
  });

  const [isMaximized, setIsMaximized] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const SCREEN_NAME = 'SalesOrder';

  // Add permissions hook
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  
  // Calculate permissions for MRN, PO and Delivery Note
  const canViewMRN = hasFullAccess || hasPermission('MRN_MASTER', 'CanView');
  const canViewPO = hasFullAccess || hasPermission('PURCHASE_ORDER', 'CanView');
  const canViewDeliveryNote = hasFullAccess || hasPermission('DELIVERY_NOTE', 'CanView');
  const canViewSales = hasFullAccess || hasPermission('SALES_ORDER', 'CanView');

  // Function to show the error popup
  const showErrorPopup = (message) => {
    setErrorPopup({
      isVisible: true,
      message
    });
  };
  
  // Function to hide the error popup
  const hideErrorPopup = () => {
    setErrorPopup({
      isVisible: false,
      message: ''
    });
  };

  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };

  const shouldShowDepartmentOnHeader = (cfg) => {
    if (!cfg) return false;
    const showHeader = truthy(cfg.ShowOnHeader) || truthy(cfg.showOnHeader);
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    return showHeader && showScreen;
  };

  const shouldShowDepartmentOnItems = (cfg) => {
    if (!cfg) return false;
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    const showItem = truthy(cfg.ShowOnItem) || truthy(cfg.showOnItem);
    return showScreen && showItem;
  };

  const formatDepartment = (name, id, code) => {
    const toStr = (v) => (v === null || v === undefined ? '' : String(v).trim());
    const firstToken = (s) => {
      if (!s) return '';
      const token = s.split(',').map(t => t.trim()).filter(Boolean)[0] || '';
      return token;
    };

    const cleanName = toStr(name);
    const cleanIdRaw = Array.isArray(id) ? toStr(id[0]) : toStr(id);
    const cleanId = firstToken(cleanIdRaw);
    const cleanCode = Array.isArray(code) ? toStr(code[0]) : toStr(code);

    const left = [cleanName, cleanId].filter(Boolean).join(' ').trim();
    const built = [left, cleanCode].filter(Boolean).join(' , ').trim();

    return built || '-';
  };

  const fetchScreenConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        let body = null;
        try { body = await res.json(); } catch (e) {}
        const msg = body?.message ? body.message : `Server returned ${res.status}`;
        throw new Error(msg);
      }
      const data = await res.json();
      const cfg = Array.isArray(data) ? (data[0] || null) : data;
      console.log('Screen info.....', cfg);
      setScreenConfig(cfg);
      return cfg;
    } catch (err) {
      console.error('fetchScreenConfig error:', err);
      setScreenConfig(null);
      return null;
    }
  };

  useEffect(() => {
    if (open) {
      setIsMaximized(false);
      setShowTrackingPanel(false);
      setActiveDocumentTab(null); // Reset document tab state
      fetchDropdownData();
      fetchScreenConfig();
      
      if (soId?.SalesOrderNumber || soId?.salesOrderNumber) {
        const soNumber = soId.SalesOrderNumber || soId.salesOrderNumber;
        fetchSODetails(soNumber);
        fetchTrackingData(soNumber);
      }
    }
  }, [open, soId]);
  
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  const toggleTrackingPanel = () => {
    const newState = !showTrackingPanel;
    setShowTrackingPanel(newState);
    // Maximize when tracking panel is opened
    if (newState && !isMaximized) {
      setIsMaximized(true);
    }
  };
  
  const fetchDropdownData = async () => {
    try {
      const response = await axios.get(`${API_URL}/sales-order/form-data`);
      setDropdownData({
        customers: response.data.customers || [],
        products: response.data.products || [],
      });
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };
  
  const fetchSODetails = async (salesOrderNumber) => {
    setLoading(true);
    setError(null);
    try {
      console.log(`Fetching SO details for: ${salesOrderNumber}`);
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API_URL}/sales-order/${salesOrderNumber}?t=${timestamp}`);
      console.log('SO details:', response.data);
      
      setSoDetails(response.data);
    } catch (error) {
      console.error('Error fetching SO details:', error);
      setError('Failed to load sales order details');
      setSoDetails(null);
    }
    setLoading(false);
  };
  
  const fetchTrackingData = async (salesOrderNumber) => {
    setTrackingLoading(true);
    setTrackingError(null);
    try {
      const response = await axios.get(`${API_URL}/sales-order-tracking/${salesOrderNumber}`);
      setTrackingData(response.data);
    } catch (error) {
      console.error('Error fetching tracking data:', error);
      setTrackingError('Failed to load tracking information');
    } finally {
      setTrackingLoading(false);
    }
  };
  
  const getCustomerName = (so) => {
    return so.CustomerName || so.customer_name || so.customerName || '-';
  };
  
  const getUOMName = (uomID) => {
    if (!uomID) return '-';
    const product = dropdownData.products.find(p => p.UOMID === uomID);
    return product ? product.UOM : uomID;
  };
  
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return parseFloat(value).toFixed(2);
  };
  
  // Improved date formatting function with debugging
  const formatDate = (date, fieldName = '') => {
    if (!date) {
      console.log(`formatDate: ${fieldName} is null or undefined`);
      return '-';
    }

    // If it's already a string in YYYY-MM-DD format, return it directly
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      console.log(`formatDate: ${fieldName} is already in YYYY-MM-DD format:`, date);
      return date;
    }

    // If it's a Date object, format it
    if (date instanceof Date) {
      const formatted = dayjs(date).format('YYYY-MM-DD');
      console.log(`formatDate: ${fieldName} is a Date object, formatted to:`, formatted);
      return formatted;
    }

    // Try to parse with dayjs
    const parsedDate = dayjs(date);
    if (!parsedDate.isValid()) {
      console.warn(`formatDate: Invalid date for ${fieldName}:`, date);
      return '-';
    }

    const formatted = parsedDate.format('YYYY-MM-DD');
    console.log(`formatDate: ${fieldName} parsed and formatted to:`, formatted);
    return formatted;
  };
  
// Replace the TrackingTree component with this new implementation
const TrackingTree = ({ data }) => {
  if (!data) return null;
  
  const { linkedPurchaseOrders, salesOrder, mrns, deliveryNotes } = data;
  
  const StatusBadge = ({ status }) => {
    const statusStyle = status === 'Completed' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-yellow-100 text-yellow-800';
      
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${statusStyle}`}>
        {status}
      </span>
    );
  };
  
  // Helper function to check if PO is approved
  const isPOApproved = (po) => {
    // Check if PO status is "approved" (case-insensitive) or isActive is truthy (1, true, "1", "true", etc.)
    return (
      (po.status && typeof po.status === 'string' && po.status.toLowerCase() === 'approved') ||
      truthy(po.isActive)
    );
  };
  
  // Enhanced Document Node Component with click handling
  const DocumentNode = ({ 
    type, 
    number, 
    date, 
    status, 
    isPending = false,
    isCenter = false,
    onClick
  }) => {
    const getIcon = () => {
      switch(type) {
        case 'sales':
          return <SalesOrderIcon className="text-white" />;
        case 'purchase':
          return <PurchaseOrderIcon className="text-white" />;
        case 'mrn':
          return <MRNIcon className={isPending ? "text-gray-400" : "text-white"} />;
        case 'delivery-note':
          return <DeliveryNoteIcon className={isPending ? "text-gray-400" : "text-white"} />;
        default:
          return null;
      }
    };
    
    const getColorClass = () => {
      if (isPending) return 'bg-gray-200';
      switch(type) {
        case 'sales':
          return 'bg-purple-500';
        case 'purchase':
          return 'bg-blue-500';
        case 'mrn':
          return 'bg-green-500';
        case 'delivery-note':
          return 'bg-yellow-500';
        default:
          return 'bg-gray-500';
      }
    };
    
    const isClickable = (type === 'purchase' && canViewPO && !isPending) || 
                       (type === 'mrn' && canViewMRN && !isPending) ||
                       (type === 'delivery-note' && canViewDeliveryNote && !isPending) ||
                       (type === 'sales' && canViewSales && !isPending);
    
    const handleClick = () => {
      if (isClickable && onClick) {
        onClick(type, number);
      } else if (!isClickable && !isPending) {
        if (type === 'purchase') {
          showErrorPopup("You don't have permission to view Purchase Orders");
        } else if (type === 'mrn') {
          showErrorPopup("You don't have permission to view MRNs");
        } else if (type === 'delivery-note') {
          showErrorPopup("You don't have permission to view Delivery Notes");
        }
      }
    };
    
    return (
      <div 
        className={`flex flex-col items-center cursor-pointer hover:opacity-80 transition-opacity ${isCenter ? 'mb-8' : ''}`}
        onClick={handleClick}
        title={isClickable ? `View ${type === 'purchase' ? 'Purchase Order' : type === 'mrn' ? 'MRN' : 'Delivery Note'} in new tab` : 
              isPending ? 'Not created yet' : 'No permission to view'}
      >
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getColorClass()}`}>
          {getIcon()}
        </div>
        <div className={`mt-2 text-center ${isPending ? 'text-gray-400' : 'text-gray-800'}`}>
          <div className="font-medium text-sm">
            {type === 'sales' && 'Sales Order'}
            {type === 'purchase' && 'Purchase Order'}
            {type === 'mrn' && 'MRN'}
            {type === 'delivery-note' && 'Delivery Note'}
          </div>
          <div className="font-semibold">{number}</div>
          {date && !isPending && (
            <div className="text-xs text-gray-500 mt-1">
              {dayjs(date).format('MMM DD, YYYY')}
            </div>
          )}
          {status && !isPending && <StatusBadge status={status} className="mt-2" />}
          {isPending && (
            <div className="text-xs text-gray-400 italic mt-2">Not created yet</div>
          )}
          {!isClickable && !isPending && (
            <div className="text-xs text-red-500 italic mt-1">No permission</div>
          )}
        </div>
      </div>
    );
  };
  
  // New Top Level Connector - connects Sales Order to both PO and Delivery Note branches
  const TopLevelConnector = () => {
    return (
      <div className="relative w-full h-16 flex items-center justify-center">
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Vertical line from Sales Order */}
          <line 
            x1="50%" 
            y1="0" 
            x2="50%" 
            y2="30" 
            stroke="#4B5563" 
            strokeWidth="0.3"
          />
          
          {/* Horizontal line connecting both branches */}
          <line 
            x1="25%" 
            y1="30" 
            x2="75%" 
            y2="30" 
            stroke="#4B5563" 
            strokeWidth="1"
          />
          
          {/* Vertical line down to PO branch (left) */}
          <line 
            x1="25%" 
            y1="30" 
            x2="25%" 
            y2="70" 
            stroke="#4B5563" 
            strokeWidth="0.3"
          />
          
          {/* Arrowhead for PO branch */}
          <polygon 
            points="25,70 23,63 27,63" 
            fill="#4B5563" 
          />
          
          {/* Vertical line down to Delivery Note branch (right) */}
          <line 
            x1="75%" 
            y1="30" 
            x2="75%" 
            y2="70" 
            stroke="#4B5563" 
            strokeWidth="0.3"
          />
          
          {/* Arrowhead for Delivery Note branch */}
          <polygon 
            points="75,70 73,63 77,63" 
            fill="#4B5563" 
          />
        </svg>
      </div>
    );
  };
  
  // Connector from Parent to Child Nodes
  const ParentToChildConnector = ({ childCount, isLeft = true }) => {
    return (
      <div className="relative w-full h-16 flex items-center justify-center">
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Horizontal line */}
          <line 
            x1={isLeft ? `${100 / (childCount + 1)}%` : `${100 - 100 / (childCount + 1)}%`} 
            y1="30" 
            x2={isLeft ? `${childCount * 100 / (childCount + 1)}%` : `${100 / (childCount + 1)}%`} 
            y2="30" 
            stroke="#4B5563" 
            strokeWidth="1"
          />
          
          {/* Vertical lines to each child */}
          {[...Array(childCount)].map((_, i) => {
            const x = isLeft 
              ? (i + 1) * (100 / (childCount + 1))
              : 100 - (i + 1) * (100 / (childCount + 1));
              
            return (
              <React.Fragment key={`child-line-${i}`}>
                <line 
                  x1={`${x}%`} 
                  y1="30" 
                  x2={`${x}%`} 
                  y2="70" 
                  stroke="#4B5563" 
                  strokeWidth="0.3"
                />
                {/* Arrowhead for each child */}
                <polygon 
                  points={`${x},70 ${parseFloat(x) - 1.5},63 ${parseFloat(x) + 1.5},63`} 
                  fill="#4B5563" 
                />
              </React.Fragment>
            );
          })}
        </svg>
      </div>
    );
  };
  
  // Connector from PO to its MRNs
  const PoToMrnsConnector = () => {
    return (
      <div className="relative w-full h-12 flex items-center justify-center">
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Vertical line from PO */}
          <line 
            x1="50%" 
            y1="0" 
            x2="50%" 
            y2="100" 
            stroke="#4B5563" 
            strokeWidth="0.3"
          />
          
          {/* Arrowhead */}
          <polygon
            points="50,100 47,93 53,93" 
            fill="#4B5563" 
          />
        </svg>
      </div>
    );
  };
  
  // Handle document click
  const handleDocumentClick = (docType, docNumber) => {
    console.log(`Document clicked: ${docType}, Number: ${docNumber}`);
    if (docType === 'purchase') {
      setActiveDocumentTab({
        type: 'purchase',
        number: docNumber,
        purchaseOrderNumber: docNumber,
        PurchaseOrderNumber: docNumber
      });
    } else if (docType === 'mrn') {
      setActiveDocumentTab({
        type: 'mrn',
        number: docNumber,
        mrnNumber: docNumber,
        MRNNumber: docNumber
      });
    } else if (docType === 'delivery-note') {
      setActiveDocumentTab({
        type: 'delivery-note',
        number: docNumber,
        deliveryNoteNo: docNumber,
        DeliveryNoteNo: docNumber
      });
    }
  };
  
  return (
    <div className="bg-white h-full">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-[#384959]">Document Tracking</h3>
        <button
          onClick={() => setShowTrackingPanel(false)}
          className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
          title="Hide Panel"
        >
          <HidePanelIcon size={20} />
        </button>
      </div>
      
      {trackingLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading tracking information...</p>
          </div>
        </div>
      ) : trackingError ? (
        <div className="bg-yellow-50 text-yellow-700 p-4 rounded-lg mb-6 border border-yellow-100">
          Tracking Error: {trackingError}
        </div>
      ) : data ? (
        <div className="flex flex-col items-center">
          {/* Sales Order Section (Parent Node) */}
          <DocumentNode 
            type="sales"
            number={salesOrder.salesOrderNumber}
            date={salesOrder.salesOrderDate}
            status={salesOrder.status}
            isCenter={true}
          />
          
          {/* Top Level Connector - connects Sales Order to both PO and Delivery Note branches */}
          <TopLevelConnector />
          
          {/* Branches Container */}
          <div className="w-full">
            <div className="flex justify-between">
              {/* Purchase Orders Branch (Left Child Nodes) */}
              <div className="w-1/2 pr-4">
                <div className="text-center text-sm font-medium text-gray-600 mb-2">Purchase Orders</div>
                
                {linkedPurchaseOrders && linkedPurchaseOrders.length > 0 ? (
                  <>
                    <ParentToChildConnector childCount={linkedPurchaseOrders.length} isLeft={true} />
                    
                    <div className="flex justify-center space-x-4">
                      {linkedPurchaseOrders.map(po => (
                        <div key={po.purchaseOrderNumber} className="flex flex-col items-center">
                          <DocumentNode 
                            type="purchase"
                            number={po.purchaseOrderNumber}
                            date={po.purchaseOrderDate}
                            status={po.status}
                            onClick={handleDocumentClick}
                          />
                          
                          {/* Only show MRNs section if PO is NOT approved */}
                          {!isPOApproved(po) && (
                            <div className="mt-4">
                              <div className="text-center text-xs font-medium text-gray-500 mb-2">MRNs</div>
                              
                              {/* Connector from PO to MRNs */}
                              <PoToMrnsConnector />
                              
                              {/* MRNs */}
                              {mrns && mrns.length > 0 ? (
                                <div className="flex justify-center space-x-2">
                                  {mrns
                                    .filter(mrn => mrn.purchaseOrderNumber === po.purchaseOrderNumber)
                                    .map(mrn => (
                                      <DocumentNode 
                                        key={mrn.mrnNumber}
                                        type="mrn"
                                        number={mrn.mrnNumber}
                                        date={mrn.mrnDate}
                                        status={typeof mrn.status === 'boolean' ? (mrn.status ? "Completed" : "Pending") : mrn.status}
                                        onClick={handleDocumentClick}
                                      />
                                    ))
                                  }
                                </div>
                              ) : (
                                <DocumentNode 
                                  type="mrn"
                                  number="MRN"
                                  isPending={true}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex justify-center">
                    <DocumentNode 
                      type="purchase"
                      number="PO"
                      isPending={true}
                    />
                  </div>
                )}
              </div>
              
              {/* Delivery Notes Branch (Right Child Nodes) */}
              <div className="w-1/2 pl-4">
                <div className="text-center text-sm font-medium text-gray-600 mb-2">Delivery Notes</div>
                
                {deliveryNotes && deliveryNotes.length > 0 ? (
                  <>
                    <ParentToChildConnector childCount={deliveryNotes.length} isLeft={false} />
                    
                    <div className="flex justify-center space-x-4">
                      {deliveryNotes.map(deliveryNote => (
                        <DocumentNode 
                          key={deliveryNote.deliveryNoteNo}
                          type="delivery-note"
                          number={deliveryNote.deliveryNoteNo}
                          date={deliveryNote.deliveryNoteDate}
                          status={deliveryNote.isActive ? "Active" : "Inactive"}
                          onClick={handleDocumentClick}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex justify-center">
                    <DocumentNode 
                      type="delivery-note"
                      number="Delivery Note"
                      isPending={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-500 py-8">
          No tracking data available
        </div>
      )}
    </div>
  );
};
  
  if (!open || !soId) return null;
  
  // Use soDetails if available, otherwise use soId
  const so = soDetails || soId;
  const soItems = soDetails?.Items || soId?.Items || [];
  
  // Inline status determination - using the exact same logic as the main table
  const isActive = soId.IsActive === 1 || soId.IsActive === true;
  const statusText = isActive ? 'Active' : 'Inactive';
  const statusStyle = isActive
    ? 'bg-green-100 text-green-800'
    : 'bg-yellow-100 text-yellow-800';

  console.log('Status determination - IsActive:', soId.IsActive, 'Type:', typeof soId.IsActive, 'isActive:', isActive, 'statusText:', statusText);
  
  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${
        open ? "opacity-100 visible" : "opacity-0 invisible"
      }`}
    >
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out forwards;
          }
        `}
      </style>
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>
      
      {/* Main Modal Container */}
      <div className={`bg-white shadow-2xl flex flex-col transition-all duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} ${isMaximized ? 'w-full h-full max-w-none m-0 rounded-none' : showTrackingPanel ? 'w-[90%] max-w-7xl' : 'w-full max-w-6xl'} h-full rounded-none`}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">
              Sales Order Details
            </h2>
            <div className="flex gap-2">
              <button 
                className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
                  showTrackingPanel 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                }`}
                onClick={toggleTrackingPanel}
                title="Track Document"
              >
                <TrackDocumentIcon size={18} />
                <span>Track Document</span>
              </button>
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={24} /> : <MaximizeIcon size={24} />}
              </button>
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1"
                onClick={onClose}
                title="Close"
              >
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Main Content Area with Sidebar Layout */}
        <div className="flex-grow overflow-hidden flex">
          {/* Main SO Details Content */}
          <div className={`flex-grow overflow-y-auto hide-scrollbar px-8 ${showTrackingPanel ? 'pr-4' : ''}`}>
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading sales order details...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
                Error: {error}
              </div>
            ) : (
              <div className="space-y-6 pb-6">
                {/* SO Header */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">
                    Sales Order Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        SO Number
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {so.SalesOrderNumber || so.salesOrderNumber || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        SO Date
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {formatDate(so.SalesOrderDate, 'SalesOrderDate')}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Customer Code
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {so.CustomerCode || so.customerCode || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Customer Name
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {getCustomerName(so)}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Customer Contact
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {so.CustomerContactName || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Status
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] flex items-center">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${statusStyle}`}>
                          {statusText}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Currency
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {so.CurrencyCode || so.currencyCode || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Delivery Date
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {formatDate(so.DeliveryDate, 'DeliveryDate')}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Payment Terms
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {so.PaymentTerm || so.paymentTerm || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Branch
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                         {Array.isArray(so.BranchCode) ? so.BranchCode[0] : so.BranchCode || '-'} {so.BranchName || ''}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Warehouse
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {so.LocationCode || so.locationCode || '-'}
                      </div>
                    </div>

                    {/* Department in header (conditional) */}
                    { (shouldShowDepartmentOnHeader(screenConfig || so?.screenConfig)) && (
                      <div className="flex flex-col gap-2 md:col-span-2">
                        <label className="block text-sm font-medium text-[#64748b]">Department</label>
                        <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                          {`${so.DepartmentCode || '-'}`}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col gap-2 md:col-span-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Remarks
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] min-h-[60px] uppercase">
                        {so.Remarks || so.remarks || '-'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Customer Order Information Section */}
                  <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
                    <h4 className="text-md font-semibold text-[#384959] mb-3">Customer Order Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">Customer Order Number</label>
                        <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                          {Array.isArray(so.CustomerOrderNumber) ? so.CustomerOrderNumber[0] : so.CustomerOrderNumber || '-'}
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">Customer Order Date</label>
                        <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {formatDate(
            Array.isArray(so.CustomerOrderDate) ? so.CustomerOrderDate[0] : so.CustomerOrderDate,
            'CustomerOrderDate'
          )}
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">Customer Due Date</label>
                        <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                          {formatDate(
            Array.isArray(so.CustomerDueDate) ? so.CustomerDueDate[0] : so.CustomerDueDate,
            'CustomerDueDate'
          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Customer Contact Display */}
                  {so.CustomerContactID && (
                    <div className="mt-6 bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <div className="flex items-center mb-2">
                        <h4 className="text-md font-semibold text-purple-800">Customer Contact</h4>
                        {so.CustomerContactID && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded uppercase">
                              {so.CustomerContactCode}
                            </span>
                          )}
                      </div>
                      <div className="text-sm text-gray-700 uppercase">
                        <p className="font-medium">{so.CustomerContactName || '-'}</p>
                        <p>{so.CustomerContactEmail || '-'}</p>
                        <p>{so.CustomerContactPhone || '-'}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Address Information Section */}
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 uppercase">
                    {/* Billing Address Display */}
                    {so.BillingAddressID || so.billingAddressID ? (
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <div className="flex items-center mb-2">
                          <h4 className="text-md font-semibold text-blue-800">Billing Address</h4>
                          {so.BillingAddressCode && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              {so.BillingAddressCode}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-700">
                          <p className="font-medium">{so.BillingContactPerson || so.BillingContactPersonName || '-'}</p>
                          <p>{so.BillingContactNumber || '-'}</p>
                          <p className="mt-1">{so.BillingAddressLine1 || '-'}</p>
                          <p>{so.BillingCity || '-'}, {so.BillingState || '-'} {so.BillingZipCode || '-'}</p>
                          <p>{so.BillingCountry || '-'}</p>
                          {so.BillingGSTNumber && (
                            <p className="mt-1">GST: {so.BillingGSTNumber}</p>
                          )}
                          {so.BillingPANNumber && (
                            <p>PAN: {so.BillingPANNumber}</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h4 className="text-md font-semibold text-gray-800 mb-2">Billing Address</h4>
                        <p className="text-sm text-gray-500">No billing address selected</p>
                      </div>
                    )}
                    
                    {/* Shipping Address Display */}
                    {so.ShippingAddressID || so.shippingAddressID ? (
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="flex items-center mb-2">
                          <h4 className="text-md font-semibold text-green-800">Shipping Address</h4>
                          {so.ShippingAddressCode && (
                            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                              {so.ShippingAddressCode}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-700">
                          <p className="font-medium">{so.ShippingContactPerson || so.ShippingContactPersonName || '-'}</p>
                          <p>{so.ShippingContactNumber || '-'}</p>
                          <p className="mt-1">{so.ShippingAddressLine1 || '-'}</p>
                          <p>{so.ShippingCity || '-'}, {so.ShippingState || '-'} {so.ShippingZipCode || '-'}</p>
                          <p>{so.ShippingCountry || '-'}</p>
                          {so.ShippingGSTNumber && (
                            <p className="mt-1">GST: {so.ShippingGSTNumber}</p>
                          )}
                          {so.ShippingPANNumber && (
                            <p>PAN: {so.ShippingPANNumber}</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h4 className="text-md font-semibold text-gray-800 mb-2">Shipping Address</h4>
                        <p className="text-sm text-gray-500">No shipping address selected</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* SO Items */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-[#384959]">
                      Ordered Items
                    </h3>
                  </div>
                  <div className="overflow-x-auto custom-scrollbar">
                    <table className="min-w-full border border-[#E2E8F0]">
                      <thead>
                        <tr className="bg-[#BDDDFC] text-[#384959]">
                          <th className="p-2">#</th>
                          <th className="p-2">Product Code</th>
                          <th className="p-2">Product Name</th>
                          <th className="p-2">Description</th>
                          <th className="p-2">UOM</th>
                          <th className="p-2">Quantity</th>
                          <th className="p-2">Remaining Qty</th>
                          <th className="p-2">Unit Price</th>
                          <th className="p-2">Discount</th>
                          <th className="p-2">Tax</th>
                          <th className="p-2">Tax Rate</th>
                          <th className="p-2">Tax Amount</th>

                          {/* Conditional header for Department column on items */}
                          {shouldShowDepartmentOnItems(screenConfig || so?.screenConfig) && (
                            <th className="p-2">Department</th>
                          )}

                          <th className="p-2">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {soItems.length > 0 ? (
                          soItems.map((item, idx) => {
                            // Find the selected tax code to display its rate
                            const taxRate = item.TaxPercentage ? parseFloat(item.TaxPercentage) : 0;
                            
                            return (
                              <tr key={idx} className="border-t border-[#E2E8F0] uppercase">
                                <td className="p-2 text-center">{idx + 1}</td>
                                <td className="p-2">
                                  <div className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {item.ProductCode || item.productCode || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {item.ProductName || item.productName || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {item.Description || item.description || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-20 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {getUOMName(item.UOMID || item.uomID)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {item.Quantity || item.quantity || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {item.RemainingQty || item.remainingQty || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {formatCurrency(item.UnitPrice || item.unitPrice)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {formatCurrency(item.Discount || item.discount)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {item.TaxName || item.taxName || item.TaxCode || item.taxCode || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {taxRate}%
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {formatCurrency(item.TaxAmount || item.taxAmount)}
                                  </div>
                                </td>

                                {/* Conditional cell for Department per item */}
                                {shouldShowDepartmentOnItems(screenConfig || so?.screenConfig) && (
                                  <td className="p-2">
                                    <div className="w-40 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                      {`${item.DepartmentCode || '-'}`}
                                    </div>
                                  </td>
                                )}

                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] font-semibold">
                                    {formatCurrency(item.Total || item.total)}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan={shouldShowDepartmentOnItems(screenConfig || so?.screenConfig) ? 14 : 13}
                              className="text-center text-gray-500 py-4"
                            >
                              No items found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Total Summary Section */}
                  <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                    <h4 className="text-md font-semibold text-[#384959] mb-4">
                      Total Summary
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Subtotal</span>
                        <span className="text-base font-semibold">
                          {formatCurrency(so.SubTotal || so.subTotal)}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Total Discount</span>
                        <span className="text-base font-semibold">
                          {formatCurrency(so.TotalDiscount || so.totalDiscount)}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Total Tax</span>
                        <span className="text-base font-semibold">
                          {formatCurrency(so.TotalTax || so.totalTax)}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Grand Total</span>
                        <span className="text-base font-semibold text-blue-600">
                          {formatCurrency(so.TotalAmount || so.totalAmount)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Tracking Sidebar */}
          {showTrackingPanel && (
            <div className="border-l overflow-y-auto hide-scrollbar bg-white rounded-xl p-6 border border-gray-200 h-full" style={{width : "50vw"}}>
              <TrackingTree data={trackingData} />
            </div>
          )}
        </div>
        
        {/* Fixed Footer with Button */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
      
      {/* Document Tab Modals */}
      {activeDocumentTab && activeDocumentTab.type === 'purchase' && (
        <PurchaseOrderViewModal
          open={!!activeDocumentTab}
          onClose={() => setActiveDocumentTab(null)}
          poId={activeDocumentTab}
        />
      )}
      
      {activeDocumentTab && activeDocumentTab.type === 'mrn' && (
        <MaterialReceiptViewModal
          open={!!activeDocumentTab}
          onClose={() => setActiveDocumentTab(null)}
          mrnId={activeDocumentTab}
        />
      )}
      
      {activeDocumentTab && activeDocumentTab.type === 'delivery-note' && (
        <DeliveryNoteViewModal
          open={!!activeDocumentTab}
          onClose={() => setActiveDocumentTab(null)}
          deliveryNoteId={activeDocumentTab}
        />
      )}
      
      {/* Modern Error Popup */}
      <ErrorPopup 
        message={errorPopup.message} 
        isVisible={errorPopup.isVisible} 
        onClose={hideErrorPopup} 
      />
    </div>
  );
};

export default SalesOrderViewModal;